export type AudioPlaybackKind = 'oneshot' | 'loop' | 'music' | 'bed';

export type AudioDistanceModel = 'inverse' | 'linear' | 'exponential';

export type AudioSpatialConfig = {
  distanceModel: AudioDistanceModel;
  refDistance: number;
  maxDistance: number;
  rolloffFactor: number;
};

export type AudioVariant = {
  name: string;
  /** Seconds into the variant's audio file where playback starts. Default 0. */
  offset?: number;
  /** Duration in seconds. Default: play to end of the file. */
  duration?: number;
  /**
   * Source file name relative to the clip's directory. Authoring uses loose
   * uploaded files (`slash1.ogg`, `slash2.ogg`); the publisher rewrites this
   * to point at the bundled `clip.webm` with offset/duration filled in.
   * When omitted, the loader defaults to `clip.webm`.
   */
  file?: string;
};

export type AudioClipConfig = {
  id: string;
  name: string;
  /** Renamed from `kind` to disambiguate from `AudioClip.asset.kind` (the decoded/streaming discriminator). */
  playbackKind: AudioPlaybackKind;
  /**
   * When true, played via MediaElementAudioSourceNode (HTTP-streamed, RAM-cheap, lossy
   * loop seam). Use for long music/bed tracks. Otherwise decoded into an AudioBuffer for
   * sample-accurate looping and sub-clip variants.
   */
  streaming: boolean;
  /**
   * One entry per variant. Single-variant clips can leave offset/duration unset
   * to play the whole referenced file.
   *
   * The bundled file name is a publisher convention (`clip.webm`); variants that
   * reference different uploaded source files (authoring with loose .ogg/.wav)
   * set `variant.file` directly. There is no clip-level "default file" —
   * removing the configuration burden was intentional.
   */
  variants: AudioVariant[];
  /** Linear gain multiplier baked at fire time (>=0). */
  gain: number;
  /** Random playbackRate range, inclusive on both ends. [1, 1] disables pitch variation. */
  pitchRange: [number, number];
  /** Per-clip live-voice cap. Older voices of the same clip are stolen first. */
  maxConcurrent: number;
  /** Null = non-spatial 2D clip; ignored by panner setup. */
  spatial: AudioSpatialConfig | null;
};

export function defaultAudioClipConfig(id: string, name: string): AudioClipConfig {
  return {
    id,
    name,
    playbackKind: 'oneshot',
    streaming: false,
    variants: [],
    gain: 1.0,
    pitchRange: [1.0, 1.0],
    maxConcurrent: 8,
    spatial: null,
  };
}

/**
 * Returns the file name a variant resolves to. After publish, every variant
 * plays from the bundled `clip.webm`; during authoring, variants can point at
 * loose uploads (e.g. `slash1.ogg`) by setting `variant.file`. Centralized so
 * the publisher convention has one definition.
 */
export function variantFile(variant: AudioVariant): string {
  return variant.file ?? 'clip.webm';
}
